import { MetabaseError, SSO_NOT_ALLOWED } from "embedding-sdk-bundle/errors";
import * as MetabaseErrors from "embedding-sdk-bundle/errors";
import type { SqlParameterValues } from "embedding-sdk-bundle/types";
import { PLUGIN_EMBED_JS_EE } from "metabase/embedding/embedding-iframe-sdk/plugin";
import type {
  EmbedAuthManager,
  EmbedAuthManagerContext,
} from "metabase/embedding/embedding-iframe-sdk/types/auth-manager";
import type { ComponentToAttributes } from "metabase/embedding/embedding-iframe-sdk/types/modular-embedding";
import type { ParameterValues } from "metabase/embedding-sdk/types/dashboard";
import { decodeJwt } from "metabase/utils/jwt";

import { debouncedReportAnalytics } from "./analytics";
import {
  ALLOWED_EMBED_SETTING_KEYS_MAP,
  DISABLE_UPDATE_FOR_KEYS,
  EMBED_JS_IFRAME_IDENTIFIER_QUERY_PARAMETER_NAME,
  METABASE_CONFIG_IS_PROXY_FIELD_NAME,
} from "./constants";
import type {
  SdkIframeEmbedElementSettings,
  SdkIframeEmbedEvent,
  SdkIframeEmbedEventHandler,
  SdkIframeEmbedMessage,
  SdkIframeEmbedSettings,
  SdkIframeEmbedTagMessage,
} from "./types/embed";
import { listenForEajsMessages } from "./utils/post-message";
import { attributeToSettingKey, parseAttributeValue } from "./webcomponents";

// Import EE Iframe Embedding script plugins
import "sdk-iframe-embedding-script-ee-plugins";

const EMBEDDING_ROUTE = "embed/sdk/v1";

/** list of active embeds, used to know which embeds to update when the global config changes */
const _activeEmbeds: Set<MetabaseEmbedElement> = new Set();

/** counter used as a parameter in the iframe src to force parallel loading */
let _iframeCounter = 0;

// Setup a proxy to watch for changes to window.metabaseConfig and update all
// active embeds when the config changes. It also setups a setter for
// window.metabaseConfig to re-create the proxy if the whole object is replaced,
// for example if this script is loaded before the customer calls
// `defineMetabaseConfig` in their code, which replaces the entire object.
export const setupConfigWatcher = () => {
  const createProxy = (target: Record<string, unknown>) =>
    new Proxy(target, {
      get(target, prop, receiver) {
        // Needed for EmbedJS Wizard to call setupConfigWatcher on the Wizard reinitialization
        if (prop === METABASE_CONFIG_IS_PROXY_FIELD_NAME) {
          return true;
        }

        return Reflect.get(target, prop, receiver);
      },
      set(metabaseConfig, prop, newValue) {
        metabaseConfig[prop as string] = newValue;
        updateAllEmbeds({ [prop]: newValue });
        return true;
      },
    });

  let currentConfig = (window as any).metabaseConfig || {};
  let proxyConfig: Record<string, unknown> = createProxy(currentConfig);

  Object.defineProperty(window, "metabaseConfig", {
    configurable: true,
    enumerable: true,
    get() {
      return proxyConfig;
    },
    set(newVal: Record<string, unknown>) {
      assertFieldCanBeUpdated(newVal);
      assertValidMetabaseConfigField(newVal);

      currentConfig = { ...currentConfig, ...newVal };
      proxyConfig = createProxy(currentConfig);
      updateAllEmbeds(currentConfig);
    },
  });

  // Trigger initial update if there was existing config
  if (Object.keys(currentConfig).length > 0) {
    updateAllEmbeds(currentConfig);
  }
};

export const updateAllEmbeds = (
  config: Partial<SdkIframeEmbedElementSettings>,
) => {
  assertFieldCanBeUpdated(config);

  _activeEmbeds.forEach((embedElement) => {
    embedElement._updateSettings(config);
  });
};

const registerEmbed = (embed: MetabaseEmbedElement) => {
  _activeEmbeds.add(embed);
  debouncedReportAnalytics(_activeEmbeds);
};

const unregisterEmbed = (embed: MetabaseEmbedElement) => {
  _activeEmbeds.delete(embed);
};

if (typeof window !== "undefined") {
  setupConfigWatcher();
}

const raiseError = (message: string) => {
  throw new MetabaseError("EMBED_TAG_ERROR", message);
};

function assertFieldCanBeUpdated(
  newValues: Partial<SdkIframeEmbedElementSettings>,
) {
  const currentConfig = (window as any).metabaseConfig || {};
  for (const field of DISABLE_UPDATE_FOR_KEYS) {
    if (
      currentConfig[field] !== undefined &&
      newValues[field] !== undefined && // we allow passing a partial update
      currentConfig[field] !== newValues[field]
    ) {
      raiseError(`${field} cannot be updated after the embed is created`);
    }
  }
}

type AllowedMetabaseConfigKey =
  (typeof ALLOWED_EMBED_SETTING_KEYS_MAP.base)[number];

function assertValidMetabaseConfigField(
  newValues: Partial<SdkIframeEmbedElementSettings>,
) {
  for (const field in newValues) {
    if (
      !ALLOWED_EMBED_SETTING_KEYS_MAP.base.includes(
        field as AllowedMetabaseConfigKey,
      )
    ) {
      raiseError(`${field} is not a valid configuration name`);
    }
  }
}

export abstract class MetabaseEmbedElement<T extends string[] = string[]>
  extends HTMLElement
  implements EmbedAuthManagerContext
{
  private _iframe: HTMLIFrameElement | null = null;
  protected abstract _componentName: string;
  protected abstract _attributeNames: T;

  static readonly VERSION = "1.1.0";

  private _isEmbedReady: boolean = false;
  private _eventHandlers: Map<
    SdkIframeEmbedEvent["type"],
    Set<SdkIframeEmbedEventHandler>
  > = new Map();
  private _authManager: EmbedAuthManager | null = null;
  private _removeMessageListener: (() => void) | null = null;

  ["custom-context"]: unknown;

  constructor() {
    super();
    this["custom-context"] = undefined;
  }

  get globalSettings() {
    return (window as any).metabaseConfig || {};
  }

  // returns the attributes converted to camelCase + global settings
  get properties(): SdkIframeEmbedElementSettings {
    const attributesConverted = this._attributeNames.reduce(
      (acc, attr) => {
        const attrValue = this.getAttribute(attr as string);
        if (attrValue !== null) {
          const key = attributeToSettingKey(attr as string);
          acc[key] = parseAttributeValue(attrValue);
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );

    return {
      ...this.globalSettings,
      ...attributesConverted,
      componentName: this._componentName,
      _isLocalhost: this._getIsLocalhost(),
      _embedReferrer: window.location.href,
    } as SdkIframeEmbedElementSettings;
  }

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | SdkIframeEmbedEventHandler,
    options?: boolean | AddEventListenerOptions,
  ): void {
    if (type === "ready") {
      const eventType = type as SdkIframeEmbedEvent["type"];
      const handler = listener as SdkIframeEmbedEventHandler;
      if (!this._eventHandlers.has(eventType)) {
        this._eventHandlers.set(eventType, new Set());
      }

      // For the ready event, invoke the handler immediately if the embed is already ready.
      if (eventType === "ready" && this._isEmbedReady) {
        handler();
        return;
      }

      this._eventHandlers.get(eventType)!.add(handler);
      return;
    }

    // Fall back to the native HTMLElement event mechanism for all other events.
    super.addEventListener(
      type,
      listener as EventListenerOrEventListenerObject,
      options,
    );
  }

  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | SdkIframeEmbedEventHandler,
    options?: boolean | EventListenerOptions,
  ): void {
    if (type === "ready") {
      const eventType = type as SdkIframeEmbedEvent["type"];
      const handler = listener as SdkIframeEmbedEventHandler;
      const handlers = this._eventHandlers.get(eventType);

      if (handlers) {
        handlers.delete(handler);

        if (handlers.size === 0) {
          this._eventHandlers.delete(eventType);
        }
      }
      return;
    }

    super.removeEventListener(
      type,
      listener as EventListenerOrEventListenerObject,
      options,
    );
  }

  /**
   * Send a message with the new settings
   */
  _updateSettings(settings: Partial<SdkIframeEmbedElementSettings>) {
    const newValues = {
      ...this.properties,
      ...settings,
    } as SdkIframeEmbedElementSettings;

    // If the iframe isn't ready yet, don't send the message now.
    if (!this._isEmbedReady) {
      return;
    }

    this._validateEmbedSettings(newValues);

    // Iframe is ready – propagate the delta
    this.sendMessage(
      "metabase.embed.setSettings",
      // When we properly fix the type for Exploration template which uses `questionId: "new"` on the custom element, we should remove this type casting.
      newValues as SdkIframeEmbedSettings,
    );
  }

  destroy() {
    this._removeMessageListener?.();
    this._removeMessageListener = null;
    this._isEmbedReady = false;
    this._eventHandlers.clear();
    this._authManager = null;

    if (this._iframe) {
      this._iframe.remove();
      this._iframe = null;
    }
  }

  connectedCallback() {
    this.style.display = "block";

    if (this._iframe) {
      // already initialised
      return;
    }

    if (!this.id) {
      this.id = `metabase-embed-${Math.random().toString(36).slice(2)}`;
    }

    try {
      this._setup();

      registerEmbed(this);
    } catch (error) {
      console.error("[metabase.embed.error]", error);
    }
  }

  disconnectedCallback() {
    this.destroy();
    unregisterEmbed(this);
  }

  attributeChangedCallback(
    attrName: string,
    oldVal: string | null,
    newVal: string | null,
  ) {
    if (!this._iframe || oldVal === newVal) {
      return;
    }

    const key = attributeToSettingKey(
      attrName,
    ) as keyof SdkIframeEmbedElementSettings;
    if (
      (DISABLE_UPDATE_FOR_KEYS as readonly string[]).includes(key as string)
    ) {
      console.error(`${key} cannot be updated after the embed is created`);
      return;
    }

    this._updateSettings({
      [key]: parseAttributeValue(newVal),
    } as Partial<SdkIframeEmbedElementSettings>);
  }

  private _emitEvent(event: SdkIframeEmbedEvent) {
    const handlers = this._eventHandlers.get(event.type);

    if (handlers) {
      handlers.forEach((handler) => handler());
    }
  }

  private _setup() {
    this._validateEmbedSettings(this.properties);

    // Initialize the auth manager
    this._authManager = PLUGIN_EMBED_JS_EE.EmbedAuthManager
      ? new PLUGIN_EMBED_JS_EE.EmbedAuthManager(this)
      : null;

    this._iframe = document.createElement("iframe");
    // Random query param is needed to allow parallel EmbedJS iframes loading.
    // Without it multiple EmbedJS iframes on a page loaded sequentially.
    // We don't cache the iframe content, so random query parameter does not break caching.
    this._iframe.src = `${this.globalSettings.instanceUrl}/${EMBEDDING_ROUTE}?${EMBED_JS_IFRAME_IDENTIFIER_QUERY_PARAMETER_NAME}=${_iframeCounter++}`;
    this._iframe.style.width = "100%";
    this._iframe.style.height = "100%";
    this._iframe.style.border = "none";
    this._iframe.style.minHeight = "600px";

    this._iframe.setAttribute("data-metabase-embed", "true");

    this._removeMessageListener = listenForEajsMessages({
      messageSource: "iframe-content",
      iframe: this._iframe,
      handler: this._handleMessage,
    });

    this.appendChild(this._iframe);
  }

  private _getIsLocalhost() {
    const { hostname } = window.location;

    try {
      if (!this.globalSettings.instanceUrl) {
        // if not configured yet, we return true to avoid throwing
        return true;
      }

      const instanceUrl = new URL(this.globalSettings.instanceUrl);

      if (hostname === instanceUrl.hostname) {
        return true;
      }
    } catch (error) {
      console.error("unable to construct the URL:", error);
    }

    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "[::1]"
    );
  }

  private _validateEmbedSettings(settings: SdkIframeEmbedElementSettings) {
    if (!settings.instanceUrl) {
      raiseError("instanceUrl must be provided");
    }

    // Ensure auth methods are mutually exclusive
    const authMethods = [
      settings.apiKey,
      settings.useExistingUserSession,
      settings.preferredAuthMethod,
    ].filter(
      (method) => method !== undefined && method !== null && method !== false,
    );

    if (authMethods.length > 1) {
      raiseError(
        "apiKey, useExistingUserSession, and preferredAuthMethod are mutually exclusive, only one can be specified.",
      );
    }
  }

  private _handleMessage = async (message: SdkIframeEmbedTagMessage) => {
    if (message.type === "metabase.embed.iframeReady") {
      if (this._isEmbedReady) {
        return;
      }

      this._isEmbedReady = true;
      if (this._iframe) {
        // this is used from tests to await the loading of the iframe
        this._iframe.setAttribute("data-iframe-loaded", "true");
      }

      const { guestEmbedProviderUri, token } = this.properties;

      // No static token provided — fetch initial guest token first, then send settings
      if (guestEmbedProviderUri && !token) {
        await this._fetchInitialGuestToken();
      } else {
        this._updateSettings(this.properties);
      }

      this._emitEvent({ type: "ready" });
    }

    if (message.type === "metabase.embed.requestSessionToken") {
      await this._authenticate();
    }

    if (message.type === "metabase.embed.requestGuestTokenRefresh") {
      await this._refreshGuestToken(message.data.expiredToken);
    }

    if (message.type === "metabase.embed.handleLink") {
      const { url, requestId } = message.data;
      const handleLink = this.globalSettings.pluginsConfig?.handleLink;

      let handled = false;
      if (typeof handleLink === "function") {
        try {
          const result = handleLink(url);
          handled = result?.handled ?? false;
        } catch (e) {
          console.error("[metabase.embed] handleLink error:", e);
        }
      }

      this._iframe?.contentWindow?.postMessage(
        {
          type: "metabase.embed.handleLinkResponse",
          data: { requestId, handled },
        },
        "*",
      );
    }

    if (message.type === "metabase.embed.parametersChange") {
      this.dispatchEvent(
        new CustomEvent("parameters-change", { detail: message.data }),
      );
    }

    if (message.type === "metabase.embed.sqlParametersChange") {
      this.dispatchEvent(
        new CustomEvent("sql-parameters-change", { detail: message.data }),
      );
    }
  };

  sendMessage<Message extends SdkIframeEmbedMessage>(
    type: Message["type"],
    data: Message["data"],
  ) {
    if (this._iframe?.contentWindow) {
      const normalizedData = Object.entries(data).reduce(
        (acc, [key, value]) => {
          // Functions are not serializable, so we ignore them.
          // `pluginsConfig` contains functions so we also skip it.
          if (typeof value === "function" || key === "pluginsConfig") {
            return acc;
          }

          acc[key as keyof typeof acc] = value;

          return acc;
        },
        {} as Message["data"],
      );

      this._iframe.contentWindow.postMessage(
        { type, data: normalizedData },
        "*",
      );
    }
  }

  private _reportAuthenticationError(error: unknown) {
    this.sendMessage("metabase.embed.reportAuthenticationError", {
      error:
        error instanceof MetabaseError
          ? error
          : MetabaseErrors.CANNOT_FETCH_JWT_TOKEN({
              url: this.properties.guestEmbedProviderUri ?? "",
              message: error instanceof Error ? error.message : String(error),
            }),
    });
  }

  private async _authenticate() {
    if (!this._authManager) {
      this._reportAuthenticationError(SSO_NOT_ALLOWED());

      return;
    }

    await this._authManager.authenticate();
  }

  private async _fetchInitialGuestToken(): Promise<void> {
    try {
      const token = await this._callGuestTokenProvider();
      this._updateSettings({
        token,
        /**
         * Clear these so SdkIframeEmbedRoute routes via the guest token branch, not the
         * other branches (which matches on dashboardId/questionId). This applies to the
         * call below too.
         */
        dashboardId: undefined,
        questionId: undefined,
      });
    } catch (error) {
      this._reportAuthenticationError(error);
      // Send settings without a token so ComponentProvider can mount and display the error.
      this._updateSettings({
        dashboardId: undefined,
        questionId: undefined,
      });
    }
  }

  private async _refreshGuestToken(expiredToken: string): Promise<void> {
    try {
      const token = await this._callGuestTokenProvider(expiredToken);
      this.sendMessage("metabase.embed.submitRefreshedGuestToken", {
        guestToken: token,
      });
    } catch (error) {
      this._reportAuthenticationError(error);
    }
  }

  /**
   * Handles token refresh, and the initial token fetch if no static token is provided.
   * Unlike the SSO counterpart which lives in the plugin system (AuthManager.ts),
   * guest embeds are OSS so this is implemented directly here in embed.ts.
   */
  private async _callGuestTokenProvider(
    expiredToken?: string,
  ): Promise<string> {
    const { guestEmbedProviderUri, componentName, dashboardId, questionId } =
      this.properties;

    if (!guestEmbedProviderUri) {
      throw MetabaseErrors.CANNOT_FETCH_JWT_TOKEN({
        url: String(guestEmbedProviderUri),
        message: "Guest embed provider URI is not configured.",
      });
    }

    const guestEmbedProviderUriFullPath = new URL(
      guestEmbedProviderUri,
      window.location.origin,
    );
    guestEmbedProviderUriFullPath.searchParams.set("response", "json");

    const entityType =
      componentName === "metabase-dashboard" ? "dashboard" : "question";

    const isRefreshingToken = expiredToken !== undefined;

    // Prefer the attribute resource ID; fall back to decoding the expired token
    // for the static token case (no dashboardId/questionId attribute).
    const attributeResourceId =
      componentName === "metabase-dashboard" ? dashboardId : questionId;
    const tokenResourceId = isRefreshingToken
      ? decodeJwt(expiredToken)?.resource?.[entityType]
      : undefined;
    const resourceId = attributeResourceId ?? tokenResourceId;

    // Only works in React 19
    const objectCustomContext = this["custom-context"];
    // parseAttributeValue parses it if it's a stringified JSON
    const stringCustomContext = parseAttributeValue(
      this.getAttribute("custom-context"),
    );
    const customContext = objectCustomContext ?? stringCustomContext;
    const body = {
      entityType,
      entityId: resourceId,
      ...(customContext !== undefined && { customContext }),
    };

    const response = await fetch(guestEmbedProviderUriFullPath.toString(), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw MetabaseErrors.CANNOT_FETCH_JWT_TOKEN({
        url: guestEmbedProviderUri,
        status: String(response.status),
      });
    }

    const data = await response.json();

    if (
      data == null ||
      typeof data !== "object" ||
      typeof data.jwt !== "string"
    ) {
      throw MetabaseErrors.DEFAULT_ENDPOINT_ERROR({
        actual: JSON.stringify(data),
      });
    }

    return data.jwt;
  }

  // `parameters` and `sqlParameters` are JS properties whose value
  // lives in the element's attribute of the same name. Reading the
  // property parses the attribute as JSON; writing the property
  // serializes the value back into the attribute. So setting the
  // attribute directly (`<metabase-dashboard parameters='{"state":"NY"}'>`),
  // assigning the JS property (`el.parameters = ...`).

  /**
   * Reads the named element attribute and returns it as a plain object.
   * Uses the same `parseAttributeValue` parser as `attributeChangedCallback`
   * Returns `undefined` if the attribute isn't set, fails to parse,
   * or doesn't resolve to a plain object (e.g. a primitive or array).
   */
  protected _readJsonAttribute<T>(attributeName: string): T | undefined {
    const rawValue = this.getAttribute(attributeName);

    if (rawValue === null) {
      return undefined;
    }

    const parsed = parseAttributeValue(rawValue);

    if (
      parsed === null ||
      typeof parsed !== "object" ||
      Array.isArray(parsed)
    ) {
      return undefined;
    }

    return parsed as T;
  }

  /**
   * Serializes a value as JSON and writes it to the named element
   * attribute. `null` or `undefined` removes the attribute. If the
   * resulting JSON string equals what's already on the attribute, our
   * `attributeChangedCallback` short-circuits on `oldVal === newVal`
   * , so we bypass `setAttribute` and dispatch `_updateSettings` directly to
   * keep the iframe in sync with the caller's intent.
   */
  protected _writeJsonProperty(
    settingKey: string,
    attrName: string,
    value: unknown,
  ) {
    // `null` and `undefined` mean "clear this property"; anything else
    // gets serialized as JSON. `nextJson === null` is the marker for
    // "the attribute should not exist".
    const shouldRemoveValue = value === undefined || value === null;
    const nextJson = shouldRemoveValue ? null : JSON.stringify(value);
    const currentJson = this.getAttribute(attrName);

    if (nextJson === currentJson) {
      // Both null: clearing something that was never set. Nothing to do.
      if (nextJson === null) {
        return;
      }

      // Same JSON string is already on the attribute, so calling
      // `setAttribute` would be a no-op for `attributeChangedCallback`
      // (it short-circuits on `oldVal === newVal`, see line ~323). The
      // host explicitly assigned the property, so bypass the attribute
      // and dispatch `_updateSettings` directly to keep the iframe
      // synced with the caller's intent.
      this._updateSettings({
        [settingKey]: value,
      } as Partial<SdkIframeEmbedElementSettings>);

      return;
    }

    // Attribute is being added, removed, or replaced — mutating it
    // fires `attributeChangedCallback`, which then calls `_updateSettings`.
    if (nextJson === null) {
      this.removeAttribute(attrName);
    } else {
      this.setAttribute(attrName, nextJson);
    }
  }
}

type ConcreteEmbedElementConstructor<U extends string[]> = new (
  ...args: any[]
) => MetabaseEmbedElement<U> & {
  _componentName: string;
  _attributeNames: U;
};

function createCustomElement<
  T extends keyof ComponentToAttributes,
  U extends (keyof ComponentToAttributes[T] & string)[],
  C extends ConcreteEmbedElementConstructor<U> =
    ConcreteEmbedElementConstructor<U>,
>(
  componentName: T,
  attributeNames: U,
  decorate?: (Base: ConcreteEmbedElementConstructor<U>) => C,
): C {
  const Base = class extends MetabaseEmbedElement<U> {
    protected _componentName: string = componentName;
    protected _attributeNames: U = attributeNames;

    static get observedAttributes() {
      return attributeNames;
    }
  } as unknown as ConcreteEmbedElementConstructor<U>;

  const CustomEmbedElement = (decorate ? decorate(Base) : Base) as C;

  if (typeof window !== "undefined" && !customElements.get(componentName)) {
    customElements.define(componentName, CustomEmbedElement);
  }

  return CustomEmbedElement;
}

export const MetabaseDashboardElement = createCustomElement(
  "metabase-dashboard",
  [
    "dashboard-id",
    "token",
    "auto-refresh-interval",
    "with-title",
    "with-downloads",
    "with-subscriptions",
    "drills",
    "initial-parameters",
    "parameters",
    "hidden-parameters",
    "enable-entity-navigation",
  ],
  (Base) =>
    class extends Base {
      get parameters(): ParameterValues | undefined {
        return this._readJsonAttribute<ParameterValues>("parameters");
      }
      set parameters(values: ParameterValues | undefined) {
        this._writeJsonProperty("parameters", "parameters", values);
      }
    },
);
export type MetabaseDashboardElement = InstanceType<
  typeof MetabaseDashboardElement
>;

export const MetabaseQuestionElement = createCustomElement(
  "metabase-question",
  [
    "question-id",
    "token",
    "with-title",
    "with-downloads",
    "with-alerts",
    "drills",
    "initial-sql-parameters",
    "sql-parameters",
    "hidden-parameters",
    "is-save-enabled",
    "target-collection",
    "entity-types",
  ],
  (Base) =>
    class extends Base {
      get sqlParameters(): SqlParameterValues | undefined {
        return this._readJsonAttribute<SqlParameterValues>("sql-parameters");
      }
      set sqlParameters(values: SqlParameterValues | undefined) {
        this._writeJsonProperty("sqlParameters", "sql-parameters", values);
      }
    },
);
export type MetabaseQuestionElement = InstanceType<
  typeof MetabaseQuestionElement
>;

const MetabaseManageContentElement = createCustomElement("metabase-browser", [
  "initial-collection",
  "collection-visible-columns",
  "collection-page-size",
  "collection-entity-types",
  "data-picker-entity-types",
  "with-new-question",
  "with-new-dashboard",
  "read-only",
  "enable-entity-navigation",
]);

const MetabaseMetabotElement = createCustomElement("metabase-metabot", [
  "layout",
  "is-save-enabled",
  "target-collection",
]);

// Expose the old API that's still used in the tests, we'll probably remove this api unless customers prefer it
if (typeof window !== "undefined") {
  (window as any)["metabase.embed"] = {
    ...(window as any)["metabase.embed"],
  };
}

export { MetabaseManageContentElement, MetabaseMetabotElement };
